import React from 'react';
import { Pagination as MuiPagination, Stack, Typography, Box, IconButton } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, FirstPage, LastPage } from '@mui/icons-material';
import './index.scss';

const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    totalItems = 0,
    itemsPerPage = 10,
    showInfo = true
}) => {
    // Calculate actual total pages based on totalItems if totalPages seems incorrect
    const calculatedTotalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : totalPages;
    const actualTotalPages = Math.max(calculatedTotalPages, totalPages);

    // Don't render if there are no items
    if (totalItems === 0) {
        return null;
    }

    const handlePageChange = (event, page) => {
        if (page !== currentPage && page >= 1 && page <= actualTotalPages) {
            onPageChange(page);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            const prevPage = currentPage - 1;
            onPageChange(prevPage);
        }
    };

    const handleNextPage = () => {
        if (currentPage < actualTotalPages) {
            const nextPage = currentPage + 1;
            onPageChange(nextPage);
        }
    };

    const handleFirstPage = () => {
        if (currentPage !== 1) {
            onPageChange(1);
        }
    };

    const handleLastPage = () => {
        if (actualTotalPages > 0 && currentPage !== actualTotalPages) {
            onPageChange(actualTotalPages);
        }
    };

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
        <div className="mui-pagination-container">
            {showInfo && totalItems > 0 && (
                <Typography variant="body2" className="pagination-info">
                    Showing {startItem} to {endItem} of {totalItems} entries
                </Typography>
            )}

            <Box className="pagination-wrapper">
                <Stack direction="row" spacing={1} alignItems="center">
                    {/* First Page Button */}
                    <IconButton
                        onClick={handleFirstPage}
                        disabled={currentPage === 1}
                        size="small"
                        className="pagination-nav-btn"
                    >
                        <FirstPage />
                    </IconButton>

                    {/* Previous Page Button */}
                    <IconButton
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        size="small"
                        className="pagination-nav-btn"
                    >
                        <KeyboardArrowLeft />
                    </IconButton>

                    {/* MUI Pagination Component */}
                    <MuiPagination
                        count={actualTotalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        variant="outlined"
                        color="primary"
                        shape="rounded"
                        size="medium"
                        hidePrevButton
                        hideNextButton
                        showFirstButton={false}
                        showLastButton={false}
                    />

                    {/* Next Page Button */}
                    <IconButton
                        onClick={handleNextPage}
                        disabled={currentPage >= actualTotalPages}
                        size="small"
                        className="pagination-nav-btn"
                    >
                        <KeyboardArrowRight />
                    </IconButton>

                    {/* Last Page Button */}
                    <IconButton
                        onClick={handleLastPage}
                        disabled={actualTotalPages === 0 || currentPage === actualTotalPages}
                        size="small"
                        className="pagination-nav-btn"
                    >
                        <LastPage />
                    </IconButton>
                </Stack>
            </Box>
        </div>
    );
};

export default Pagination;
